import { FieldSpecWithId, isFieldEnabled, isKindNumeric } from 'lib/rich'

/**
 * Validates an input object against a given set of FieldSpecs.
 * Returns a validated object of type T or throws an error with details.
 */
export function validate<T extends Record<string, number>>(
  data: Partial<T>,
  specs: readonly FieldSpecWithId[],
): Record<string, string> {
  const errors: Record<string, string> = {}

  const titles = {}
  for (const spec of specs) {
    const id = spec.id
    titles[id] = spec.title
  }

  function titleFor(id) {
    return titles[id] || id
  }

  for (const spec of specs) {
    const key = spec.id

    if (!isFieldEnabled(spec, data)) {
      // Only check enabled fields
      continue
    }

    let value = (data as unknown)[key]
    // clearing the field sets its value to an empty string
    if (value === undefined || value === '') {
      if (spec.required) {
        errors[key as string] = `"${spec.title}" is required.`
      }
      // Skip further checking of non-required values
      continue
    }

    if (spec?.maxLength) {
      if (value.toString().trim().length > spec.maxLength) {
        errors[
          key as string
        ] = `"${spec.title}" must not exceed ${spec?.maxLength} characters.`
      }
      // Skip further checking of fields without maxLength
      continue
    }

    if (isKindNumeric(spec.kind)) {
      // Validate type (assuming number)
      if (typeof value !== 'number') {
        errors[key as string] = `"${spec.title}" must be a number.`
        continue
      }

      if (spec.kind === 'integer' && !Number.isInteger(value)) {
        errors[key as string] = `"${spec.title}" must be an integer.`
        continue
      }

      // Check gt - greater-than
      if (spec.gt !== undefined) {
        const ref = typeof spec.gt === 'number' ? spec.gt : data[spec.gt]
        if (!(value > ref)) {
          errors[key as string] = `${
            spec.title
          } must be greater than ${titleFor(spec.gt)}.`
          continue
        }
      }

      // Check gte - greater-than-or-equal
      if (spec.gte !== undefined) {
        const ref = typeof spec.gte === 'number' ? spec.gte : data[spec.gte]
        if (!(value >= ref)) {
          errors[key as string] = `${
            spec.title
          } must be greater than or equal ${titleFor(spec.gte)}.`
          continue
        }
      }

      // Check lt - less-than
      if (spec.lt !== undefined) {
        const ref = typeof spec.lt === 'number' ? spec.lt : data[spec.lt]
        if (!(value < ref)) {
          errors[key as string] = `${spec.title} must be less than ${titleFor(
            spec.lt,
          )}.`
          continue
        }
      }

      // Check lte - less-than-or-equal
      if (spec.lte !== undefined) {
        const ref = typeof spec.lte === 'number' ? spec.lte : data[spec.lte]
        if (!(value <= ref)) {
          errors[key as string] = `${
            spec.title
          } must be less than or equal ${titleFor(spec.lte)}.`
          continue
        }
      }
    }
  }

  const hasErrors = Object.keys(errors).length > 0
  return hasErrors ? errors : undefined
}
